<script setup>
import { ref, computed, onBeforeMount } from "vue";
import { useRoute } from "vue-router";
import confusedEmotion from "@/assets/images/emotions/confused.svg";
import { safeParse } from "@/utils/accessControl";
import { hasAnyMenuAccess } from "@/components/sideMenuItems";
import { useAuthStore } from "@/stores/auth";

const route = useRoute();
const authStore = useAuthStore();
const statusCode = ref(404);
const message = ref("Sorry, the page you're looking for doesn't exist.");

const canReachDashboard = computed(() => {
  const userDetails = safeParse(localStorage.getItem("user"));
  return hasAnyMenuAccess(userDetails, process.env.VUE_APP_SUPER_ADMIN_EMAIL);
});

onBeforeMount(() => {
  if (route.query.redirect) {
    statusCode.value = 307;
    message.value = "You accessed data that doesn't exist or you don't have access to it.";
  }
});
</script>

<template>
  <div class="void-page">
    <div class="void-inner">
      <h1 class="void-code">{{ statusCode }}</h1>

      <p class="void-message">{{ message }}</p>

      <router-link v-if="canReachDashboard" to="/" class="void-btn">
        <span>Back to dashboard</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M2.5 7h9M7.5 3l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </router-link>
      <button v-else type="button" class="void-btn" @click="authStore.logout()">
        <span>Log out</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M2.5 7h9M7.5 3l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="void-signal" aria-hidden="true">
        <div class="void-signal__radar">
          <span class="void-signal__ring void-signal__ring--1"></span>
          <span class="void-signal__ring void-signal__ring--2"></span>
          <span class="void-signal__ring void-signal__ring--3"></span>
        </div>

        <img :src="confusedEmotion" alt="" class="void-signal__icon" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.void-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 50% 0%, var(--primary-50) 0%, var(--neutral-50) 55%);
  padding: var(--spacing-8) var(--spacing-4);
}

.void-inner {
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.void-code {
  font-family: "lato-black", Lato, sans-serif;
  font-size: clamp(88px, 18vw, 148px);
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  margin: 0 0 12px;
  background: linear-gradient(180deg, var(--primary-700) 0%, var(--primary-500) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  opacity: 0;
  animation: void-rise 0.55s ease-out forwards;
}

.void-message {
  font-family: Lato, sans-serif;
  font-size: 15.5px;
  line-height: 1.6;
  color: var(--neutral-600);
  max-width: 340px;
  margin: 0 0 32px;
  opacity: 0;
  animation: void-rise 0.6s ease-out 0.1s forwards;
}

.void-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border: none;
  cursor: pointer;
  border-radius: var(--border-radius-md, 10px);
  background: var(--primary-600);
  color: #fff;
  font-family: Lato, sans-serif;
  font-size: 14.5px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 24px -12px var(--primary-600);
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
  opacity: 0;
  animation: void-rise 0.6s ease-out 0.18s forwards;
}

.void-btn svg {
  transition: transform 0.18s ease;
}

.void-btn:hover {
  background: var(--primary-700);
  transform: translateY(-1px);
  box-shadow: 0 14px 28px -12px var(--primary-700);
}

.void-btn:hover svg {
  transform: translateX(2px);
}

.void-signal {
  position: relative;
  width: 96px;
  height: 96px;
  margin-top: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  animation: void-rise 0.7s ease-out 0.28s forwards;
}

.void-signal__radar {
  position: absolute;
  inset: 0;
}

.void-signal__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid var(--primary-200);
  opacity: 0;
  animation: void-radar 3.6s ease-out infinite;
}

.void-signal__ring--2 { animation-delay: 1.2s; }
.void-signal__ring--3 { animation-delay: 2.4s; }

.void-signal__icon {
  position: relative;
  z-index: 1;
  width: 88px;
  height: 88px;
  object-fit: contain;
}

@keyframes void-rise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes void-radar {
  0% { transform: scale(0.5); opacity: 0.55; }
  100% { transform: scale(1.9); opacity: 0; }
}

@media (max-width: 480px) {
  .void-signal { transform: scale(0.88); margin-top: 32px; }
}

@media (prefers-reduced-motion: reduce) {
  .void-code,
  .void-message,
  .void-btn,
  .void-signal {
    animation: none;
    opacity: 1;
  }
  .void-signal__ring {
    animation: none;
    opacity: 0.5;
  }
}
</style>
